/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Input, Form } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import moment from "moment";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
} from "../../CommonComponents";
import ReportModernViewWrapper from "../ReportModernViewWrapper";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadBookingVsInscanOutscanReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const BookingInscanOutscanReport = () => {
  // Global request cancellation hook
  const { createNewRequest, isLatestRequest } = useRequestCancellation();

  const [headerHubList, setHeaderHubList] = useState([]);
  const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

  // Fetch hubs from header/localStorage
  useEffect(() => {
    try {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];
      if (userHubs?.length > 0) {
        const headerHubs = [
          { label: "All", value: "ALL" },
          ...userHubs.map(hub => ({
            label: hub.hubName,
            value: hub.hubId
          }))
        ];
        setHeaderHubList(headerHubs);
      } else {
        // If no hubs assigned, show all available hubs but without "All" option
        setHeaderHubList([]);
      }
    } catch (e) {
      console.error("Error parsing user hubs for Booking vs First Inscan Report", e);
      setHeaderHubList([]);
    }
  }, []);

  const handleMultiSelectChange = (values, setter, options = []) => {
    if (values.includes("ALL")) {
      const isLatestAll = values[values.length - 1] === "ALL";
      if (isLatestAll) {
        // Select all actual values (excluding the "ALL" option itself)
        const allActualValues = options
          .filter(opt => opt.value !== "ALL")
          .map(opt => opt.value);
        setter(allActualValues);
      } else {
        // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
        setter(values.filter(v => v !== "ALL"));
      }
    } else {
      setter(values);
    }
  };

  // Ref to track whether we already set the hub for the current login session
  const hubInitializedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  // Set default date range to last 7 days
  const defaultDateRange = [
    moment().subtract(1, "days"),
    moment()
  ];

  const [dateRange, setDateRange] = useState(defaultDateRange);

  // Handle date range change with validation
  const handleDateRangeChange = (dates) => {
    if (!dates || dates.length !== 2) {
      setDateRange(dates);
      setLoading(false);
      return;
    }

    const [startDate, endDate] = dates;
    const diffDays = endDate.diff(startDate, "days");

    if (diffDays > 7) {
      message.error("Date range cannot exceed 7 days");
      return;
    }

    setDateRange(dates);
  };
  const [hubList, setHubList] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [cpList, setCpList] = useState([]);
  const [selectedCps, setSelectedCps] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [awbInput, setAwbInput] = useState("");
  const [awbFilter, setAwbFilter] = useState("");
  const awbInputRef = useRef(null);
  const statusOptions = [
    { label: "inscan", value: "inscan" },
    { label: "outscan", value: "outscan" }
  ];
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

  // Fetch hub list on mount
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const hubs = await getHubList();
        setHubList(hubs);
      } catch {
        message.error("Failed to fetch hub list");
      } finally {
        // setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handleHubChanged = () => {
      const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
      const newActiveHubId = activeHub?.hubId;
      setActiveHubId(newActiveHubId || "ALL_HUBS");
      if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
        setSelectedHubs([newActiveHubId]);
      } else {
        setSelectedHubs(["ALL"]);
      }
    };

    window.addEventListener("hubChanged", handleHubChanged);
    return () => window.removeEventListener("hubChanged", handleHubChanged);
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
    const role = user?.roles?.[0];
    setHubUser(role);

    const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
    const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

    if (!hubInitializedRef.current) {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];

      if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
        setSelectedHubs([currentActiveHubId]);
      } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
        setSelectedHubs([]);
      } else {
        setSelectedHubs(["ALL"]);
      }
      hubInitializedRef.current = true;
    }
  }, [headerHubList]); // Depend on headerHubList to ensure we have options available

  // Fetch CP list when hubs change
  useEffect(() => {
    if (selectedHubs.length > 0) {
      Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)))
        .then(cpLists => {
          const allCps = [...new Set(cpLists.flat())];
          setCpList(allCps);
        })
        .catch(() => {
          message.error("Failed to fetch CP lists");
          setCpList([]);
        });
    } else {
      setCpList([]);
    }
  }, [selectedHubs]);

  // Fetch report data when filters or pagination change
  useEffect(() => {
    // Wait for hubUser to be initialized
    if (!hubUser) {
      return;
    }
    // For HUBADMIN users, hub selection is mandatory
    if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
      return; // Don't fetch if HUBADMIN has no hub selected
    }
    fetchReport();
    // eslint-disable-next-line
  }, [hubUser, selectedHubs, selectedCps, selectedStatus, dateRange, awbFilter, pagination.current, pagination.pageSize]);

  const fetchReport = async () => {
    // For HUBADMIN users, hub selection is mandatory
    if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
      return;
    }

    // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
    const { signal, requestId } = createNewRequest("booking-inscan-outscan-report");

    try {
      setLoading(true);
      setReportData([]); // Clear previous data

      // Resolve Hubs (selectedHubs) for API logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedHubIds = selectedHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedHubs;

      const selectedHubData = activeHubOptions.filter(hub => resolvedHubIds?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";

      const params = {
        ...(selectedHubData?.length > 0 && { hubNames: selectedHubData.map(hub => hub.label) }),
        ...(selectedCpData?.length > 0 && { cpNames: selectedCpData.map(cp => cp.label) }),
        ...(hasValidDates && {
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD")
        }),
        awbNumbers: awbFilter.split(",").map(awb => awb.trim()).filter(Boolean),
        audit: selectedStatus.length > 0 ? selectedStatus : [],
        page: pagination.current,
        pageSize: pagination.pageSize
      };

      const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getBookingVsInscanOutscanReport(params, { signal });

      // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        console.log(`🔥 Request ${requestId} completed successfully - updating state`);
        setReportData(data);
        setLastsynced(lastRefreshedAt);
        setNextScheduledRefresh(nextScheduledRefresh);
        setPagination(prev => ({
          ...prev,
          total,
          current: page,
          pageSize
        }));
      }

    } catch (error) {
      // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled at HTTP level - no state update");
        return;
      }

      // Only update if this is still the latest request
      if (isLatestRequest(requestId)) {
        console.error("Error fetching booking vs first inscan report:", error);
        message.error("Failed to fetch report data");
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } finally {
      // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownload = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a date range for download");
      return;
    }

    setDownloadLoading(true);
    try {
      // Resolve Hubs (selectedHubs) for Download logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedHubIds = selectedHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedHubs;

      const selectedHubData = activeHubOptions.filter(hub => resolvedHubIds?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      const downloadParams = {
        ...(selectedHubData?.length > 0 && { hubNames: selectedHubData.map(hub => hub.label) }),
        ...(selectedCpData?.length > 0 && { cpNames: selectedCpData.map(cp => cp.label) }),
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        awbNumbers: awbFilter.split(",").map(awb => awb.trim()).filter(Boolean),
        audit: selectedStatus.length > 0 ? selectedStatus : []
      };

      await downloadBookingVsInscanOutscanReport(downloadParams);
      message.success("Report downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download report");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Table columns (customize as needed)
  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    { title: "Date", dataIndex: "date", key: "date" },
    {
      title: "AWB Number", dataIndex: "awbNumber", key: "awbNumber", sorter: true,
      render: (text, record) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => {
            const url = `/shipment-tracker?awb=${text}`;
            window.open(url, "_blank");
          }}
        >
          {text}
        </span>
      )
    },
    { title: "Booking CP", dataIndex: "bookingCp", key: "bookingCp", width: 200 },
    { title: "To Hub", dataIndex: "toHub", key: "toHub", width: 200 },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Service Type", dataIndex: "serviceType", key: "serviceType" },
    { title: "Status", dataIndex: "status", key: "status", width: 80 },
    { title: "Travel By", dataIndex: "travelBy", key: "travelBy" },
    { title: "Zone", dataIndex: "zone", key: "zone" },
  ];

    return (
    <ReportModernViewWrapper
      title="Booking vs First Inscan Report"
      newReportUrl="/reports/booking-vs-first-inscan/"
      lastsynced={lastsynced}
      nextScheduledRefresh={nextScheduledRefresh}
    >
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={4}>
            <div className="filter-label">From & To Date</div>
            <CalendarComponent
              className="custom-calendar"
              type="range"
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <div className="filter-label">To hub</div>
            <DropDownComponent
              placeholder="Select To Hubs"
              options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
              onChange={(val) => {
                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                setSelectedCps([]); // Clear CP selection when hubs change
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedHubs}
              allowClear
              showSearch
              style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <div className="filter-label">Booking Cp</div>
            <DropDownComponent
              placeholder="Booking Cp"
              options={cpList}
              onChange={setSelectedCps}
              value={selectedCps}
              allowClear
              showSearch
              style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <div className="filter-label">Status</div>
            <DropDownComponent
              placeholder="Status"
              options={statusOptions}
              onChange={setSelectedStatus}
              value={selectedStatus}
              allowClear
              showSearch
              style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <div className="filter-label">AWB Number</div>
            <Form.Item style={{ marginBottom: 0 }}>
              <Input
                ref={awbInputRef}
                placeholder="Search AWB record..."
                value={awbInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setAwbInput(value);
                  if (!value) {
                    setAwbFilter("");
                  }
                }}
                onPressEnter={() => setAwbFilter(awbInput)}
                onClear={() => {
                  setAwbInput("");
                  setAwbFilter("");
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData("text");
                  // Split by newlines, tabs, or multiple spaces and filter empty strings
                  const processedText = pastedText
                    .split(/[\r\n\t ]+/)
                    .map(item => item.trim())
                    .filter(item => item !== "")
                    .join(",");

                  if (processedText) {
                    // If there's already text, append with a comma if needed
                    const newValue = awbInput
                      ? `${awbInput.trim().replace(/,+$/, "")},${processedText}`
                      : processedText;
                    setAwbInput(newValue);

                    // Scroll to end after state update
                    setTimeout(() => {
                      if (awbInputRef.current) {
                        const inputElement = awbInputRef.current.input;
                        if (inputElement) {
                          inputElement.scrollLeft = inputElement.scrollWidth;
                          // Also set cursor to end
                          const len = newValue.length;
                          inputElement.setSelectionRange(len, len);
                        }
                      }
                    }, 0);
                  }
                }}
                style={{
                  height: "32px",
                  maxHeight: "32px",
                  borderRadius: "5px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "#fff",
                  overflow: "hidden"
                }}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4} style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div className="filter-label">&nbsp;</div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              loading={downloadLoading}
              style={{ height: "32px", width: "100%", borderRadius: "5px" }}
              disabled={!dateRange || dateRange.length !== 2}
            >
              Download Report
            </Button>
          </Col>

        </Row>
      </Card>
      <TableComponent
        columns={columns}
        dataSource={reportData}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ y: "calc(100vh - 350px)", x: "max-content" }}
        style={{ flex: 1 }}
        sticky
      />
    </ReportModernViewWrapper>
  );
};

export default BookingInscanOutscanReport; 
